import React, { useEffect, useState } from 'react';
import { useTournament } from '../contexts/TournamentContext';

const TournamentStatus = () => {
  const { 
    selectedCompetition, 
    userRank, 
    getUserPortfolio, 
    activeCompetitions,
    userCompetitions,
    userPortfolios,
    exitTournament 
  } = useTournament();

  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update time every second for progress bar
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get current competition details
  const currentCompetition = activeCompetitions.find(comp => comp.competition_id === selectedCompetition);
  const portfolio = selectedCompetition ? getUserPortfolio(selectedCompetition) : null;

  // If no competition is selected, try to auto-select from user competitions
  if (!selectedCompetition && userCompetitions.length > 0) {
    return null; // Will be handled by context
  }

  if (!selectedCompetition || !currentCompetition) {
    return null;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getRankColor = (rank) => {
    if (rank <= 3) return 'text-yellow-400';
    if (rank <= 10) return 'text-green-400';
    return 'text-gray-400';
  };

  const getPnLColor = (pnl) => {
    if (pnl > 0) return 'text-green-400';
    if (pnl < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  const calculateProgress = () => {
    const startTime = currentCompetition.start_time / 1000000;
    const endTime = currentCompetition.end_time / 1000000;
    const now = currentTime / 1000000;
    return Math.min(100, Math.max(0, ((now - startTime) / (endTime - startTime)) * 100));
  };

  const progress = calculateProgress();

  const handleExitTournament = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Tournament exit clicked');
    exitTournament();
  };

  return (
    <>
      {/* Desktop Version */}
      <div className="hidden md:block bg-gradient-to-br from-atticus-navy-900/90 to-atticus-navy-800/90 backdrop-blur-sm border border-yellow-400/30 rounded-lg shadow-xl min-w-[300px] p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-gray-300 uppercase tracking-wide">
              {currentCompetition.title}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-gray-500">
              {currentCompetition.competition_type}
            </div>
            <button
              onClick={handleExitTournament}
              className="text-gray-400 hover:text-red-400 hover:bg-red-400/10 text-lg font-bold w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200"
              title="Exit Tournament"
            >
              ×
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6 mb-4">
          {/* Portfolio Value */}
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-2 font-medium">Portfolio</div>
            <div className="text-base font-bold text-white leading-tight">
              {portfolio ? formatCurrency(portfolio.current_balance) : formatCurrency(currentCompetition.trading_rules.starting_capital)}
            </div>
          </div>

          {/* P&L */}
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-2 font-medium">P&L</div>
            <div className={`text-base font-bold ${getPnLColor(portfolio?.total_pnl || 0)} leading-tight`}>
              {portfolio ? formatCurrency(portfolio.total_pnl) : '$0'}
            </div>
          </div>

          {/* Rank */}
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-2 font-medium">Rank</div>
            <div className={`text-base font-bold ${getRankColor(userRank?.current_rank || 999)} leading-tight`}>
              {userRank ? `#${userRank.current_rank}` : 'N/A'}
            </div>
          </div>
        </div>

        {/* Progress bar for competition time */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span className="font-medium">Progress</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-atticus-navy-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-green-400 to-blue-400 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Quick stats */}
        {portfolio && (
          <div className="pt-3 border-t border-atticus-navy-700">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 font-medium">Trades: {portfolio.total_trades}</span>
              <span className="text-gray-500 font-medium">Win Rate: {(portfolio.win_rate || 0).toFixed(1)}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Version - Clean and Readable */}
      <div className="md:hidden bg-gradient-to-br from-atticus-navy-900/95 to-atticus-navy-800/95 backdrop-blur-sm border border-yellow-400/30 rounded-lg p-4 shadow-xl mx-2 mb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-lg font-bold text-yellow-400 uppercase tracking-wide truncate">
              {currentCompetition.title}
            </span>
          </div>
          <button
            onClick={handleExitTournament}
            className="text-gray-400 hover:text-red-400 hover:bg-red-400/10 text-2xl font-bold w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200"
            title="Exit Tournament"
          >
            ×
          </button>
        </div>

        {/* Progress Section */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-base font-bold text-gray-300">Progress</span>
            <span className="text-xl font-bold text-yellow-400">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-atticus-navy-700 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-green-400 to-blue-400 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Portfolio & P&L */}
          <div className="text-center">
            <div className="text-base text-gray-400 mb-2 font-bold">Portfolio</div>
            <div className="text-lg font-bold text-white leading-tight mb-1">
              {portfolio ? formatCurrency(portfolio.current_balance) : formatCurrency(currentCompetition.trading_rules.starting_capital)}
            </div>
            <div className={`text-base font-bold ${getPnLColor(portfolio?.total_pnl || 0)} leading-tight`}>
              {portfolio ? formatCurrency(portfolio.total_pnl) : '$0'} P&L
            </div>
          </div>

          {/* Rank & Stats */}
          <div className="text-center">
            <div className="text-base text-gray-400 mb-2 font-bold">Rank</div>
            <div className={`text-lg font-bold ${getRankColor(userRank?.current_rank || 999)} leading-tight mb-1`}>
              {userRank ? `#${userRank.current_rank}` : 'N/A'}
            </div>
            {portfolio && (
              <div className="text-base text-gray-400 font-bold">
                {portfolio.total_trades} trades
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TournamentStatus; 